import { useCallback, useEffect, useState } from 'react';
import EditAuctionStatus from './components/EditAuctionStatus';
import EditAuctionStatusBulk from './components/EditAuctionStatusBulk';
import i18n from '../../../i18n';
import { FalseIcon, TrueIcon } from '../../common/icon';
import {
  getAllAuctionReports,
  getAllAuctionsByGroup,
  getAuctionReport,
  updateAuctionReport,
  updateAuctionReportBulk,
} from '../../../models/auctions';
import { getAllGroups } from '../../../models/groups';
import { HomeBanner } from '../../home/homeBanner';
import { SideBar } from '../sidebar';
import { MzadLoader } from '../../helpers/mzadLoader';
import DataTable from 'react-data-table-component';
import swal from 'sweetalert';
import { apiUrl } from '../../../constants/global';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
      backgroundColor: '#dadada',
      color: '#284780',
    },
  },
};

const INITIAL_FORM_DATA = {
  rop_approval: { value: false, remark: '' },
  invoice_send: { value: false, remark: '' },
  paid: { value: false, remark: '' },
  paid_guarantee: { value: false, remark: '' },
  approval_paper: { value: false, remark: '' },
  items_received: { value: false, remark: '' },
  rop_paid: { value: false, remark: '' },
  feedback: { value: false, remark: '' },
};

export default function AuctionStatus() {
  // State management
  const [hideSidebar, setHideSidebar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [auctions, setAuctions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [auctionSelected, setAuctionSelected] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  // Filter options state
  const [groups, setGroups] = useState([]);
  const [auctionOptions, setAuctionOptions] = useState([]);

  // Search and filter state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 15,
    group: '',
    auction: '',
    dateFilter: '',
    fromDate: '',
    toDate: '',
  });
  const qstring = new URLSearchParams(filters).toString();

  // Initialize filters from URL params
  useEffect(() => {
    window.scrollTo(0, 0);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    setFilters((prev) => ({
      ...prev,
      ...params,
      page: parseInt(params.page) || 1,
      limit: parseInt(params.limit) || 15,
    }));
  }, []);

  // Load filter options
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const groupsResponse = await getAllGroups();
        if (groupsResponse.success) {
          setGroups(groupsResponse.data);
        }
      } catch (error) {
        console.error('Error loading groups:', error);
      }
    };

    loadGroups();
  }, []);

  const columns = [
    {
      name: i18n.t('ID'),
      sortable: true,
      width: '70px',
      cell: (row) => row.id,
    },
    {
      name: i18n.t('auction'),
      width: '90px',
      center: true,
      cell: (row) => (
        <a href={row.images?.[0]} target="_blank" rel="noreferrer">
          <img src={row.images?.[0]} alt="Auction" style={{ width: 45, height: 45, objectFit: 'cover' }} />
        </a>
      ),
    },

    {
      name: i18n.t('auction_id'),
      width: '100px',
      center: true,
      cell: (row) => row.auction_id,
    },
    {
      name: i18n.t('auction_name'),
      width: '120px',
      center: true,
      cell: (row) => (i18n.language === 'en' ? row.title : row.title_ar),
    },
    {
      name: i18n.t('auction_group'),
      width: '120px',
      center: true,
      cell: (row) => (i18n.language === 'en' ? row.group_name : row.group_name_ar),
    },
    {
      name: i18n.t('end_date'),
      center: true,
      cell: (row) => new Date(row.end_date).toLocaleDateString('en-GB'),
    },

    {
      name: i18n.t('bidder_name'),
      cell: (row) => row.bidder_name,
    },
    {
      name: i18n.t('bidder_contact'),
      width: '120px',
      center: true,
      cell: (row) => `${row.bidder_country_code} ${row.bidder_mobile}`,
    },
    {
      name: i18n.t('rop_approval'),
      width: '120px',
      center: true,
      cell: (row) => (row.rop_approval_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('invoice_send'),
      width: '120px',
      center: true,
      cell: (row) => (row.invoice_send_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('paid'),
      center: true,
      cell: (row) => (row.paid_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('final_amount'),
      width: '120px',
      center: true,
      cell: (row) => row.total_amount,
    },
    {
      name: i18n.t('paid_guarantee'),
      width: '130px',
      center: true,
      cell: (row) => (row.paid_guarantee_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('approval_paper'),
      width: '130px',
      center: true,
      cell: (row) => (row.approval_paper_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('items_received'),
      width: '120px',
      center: true,
      cell: (row) => (row.items_received_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('rop_paid'),
      width: '120px',
      center: true,
      cell: (row) => (row.rop_paid_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('feedback'),
      width: '120px',
      center: true,
      cell: (row) => (row.feedback_at ? TrueIcon : FalseIcon),
    },
    {
      name: i18n.t('action'),
      center: true,
      cell: (row) => (
        <button className="btn btn-primary btn-sm text-white m-1" onClick={() => handleOpenModal(row)}>
          <i className="fa fa-pencil"></i>
        </button>
      ),
    },
  ];

  // Load auctions with filters
  const loadAllAuctions = useCallback(
    async (queryParams = filters) => {
      setIsLoading(true);
      try {
        const qstring = new URLSearchParams(queryParams).toString();
        const response = await getAllAuctionReports(qstring);

        if (response.success) {
          setAuctions(response.data);
          setTotalRows(response.meta?.total || 0);
        } else {
          setAuctions([]);
          setTotalRows(0);
        }
      } catch (error) {
        setAuctions([]);
        setTotalRows(0);
      } finally {
        setIsLoading(false);
      }
    },
    [filters],
  );

  // Load auctions when filters change
  useEffect(() => {
    loadAllAuctions();
  }, [loadAllAuctions]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    async (field, value) => {
      const newFilters = {
        ...filters,
        [field]: value,
        page: 1, // Reset to first page when filtering
      };

      // If group is selected, load auctions for that group
      if (field === 'group' && value) {
        try {
          const response = await getAllAuctionsByGroup(value);
          if (response.success) {
            setAuctionOptions(response.data);
          } else {
            setAuctionOptions([]); // Clear if failed
          }
        } catch (error) {
          console.error('Error loading auctions by group:', error);
          setAuctionOptions([]);
        }
      }
      // Handle date filter logic
      if (field === 'dateFilter') {
        if (value === 'last7days') {
          const toDate = new Date();
          const fromDate = new Date();
          fromDate.setDate(toDate.getDate() - 7);

          newFilters.fromDate = fromDate.toISOString().split('T')[0];
          newFilters.toDate = toDate.toISOString().split('T')[0];
        } else if (value === 'last30days') {
          const toDate = new Date();
          const fromDate = new Date();
          fromDate.setDate(toDate.getDate() - 30);

          newFilters.fromDate = fromDate.toISOString().split('T')[0];
          newFilters.toDate = toDate.toISOString().split('T')[0];
        } else if (value === 'custom') {
          // User must select 'fromDate' and 'toDate' manually
          newFilters.fromDate = '';
          newFilters.toDate = '';
        }
      }

      setFilters(newFilters);
      setCurrentPage(1);

      // Update URL
      const newUrl = `${window.location.pathname}?${new URLSearchParams(newFilters).toString()}`;
      window.history.replaceState(null, '', newUrl);
    },
    [filters],
  );

  // Handle search
  // const handleSearch = useCallback(() => {
  //   loadAllAuctions(filters);
  // }, [filters, loadAllAuctions]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      page: 1,
      limit: filters.limit,
      group: '',
      auction: '',
      dateFilter: '',
      fromDate: '',
      toDate: '',
    };
    setFilters(clearedFilters);
    setCurrentPage(1);

    // Update URL
    const newUrl = `${window.location.pathname}?${new URLSearchParams(clearedFilters).toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [filters.limit]);

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      const newFilters = { ...filters, page };
      setFilters(newFilters);
      setCurrentPage(page);
    },
    [filters],
  );

  // Handle rows per page change
  const handleRowsPerPageChange = useCallback(
    (newLimit) => {
      const newFilters = { ...filters, limit: newLimit, page: 1 };
      setFilters(newFilters);
      setCurrentPage(1);
    },
    [filters],
  );

  // Modal handlers
  const handleOpenModalBulk = useCallback(async (row) => {
    const newFormData = {
      rop_approval: { value: false, remark: '' },
      invoice_send: { value: false, remark: '' },
      paid: { value: false, remark: '' },
      paid_guarantee: { value: false, remark: '' },
      approval_paper: { value: false, remark: '' },
      items_received: { value: false, remark: '' },
      rop_paid: { value: false, remark: '' },
      feedback: { value: false, remark: '' },
    };

    setFormData(newFormData);
    setShowBulkModal(true);
  }, []);

  // Modal handlers
  const handleOpenModal = useCallback(async (row) => {
    // setIsLoading(true);
    try {
      const response = await getAuctionReport(row.id);
      if (response.success) {
        const data = response.data;

        const newFormData = {
          rop_approval: { value: !!data.rop_approval_at, remark: data.rop_approval_remark || '' },
          invoice_send: { value: !!data.invoice_send_at, remark: data.invoice_send_remark || '' },
          paid: { value: !!data.paid_at, remark: data.paid_at_remark || '' },
          paid_guarantee: { value: !!data.paid_guarantee_at, remark: data.paid_guarantee_remark || '' },
          approval_paper: { value: !!data.approval_paper_at, remark: data.approval_paper_remark || '' },
          items_received: { value: !!data.items_received_at, remark: data.items_received_remark || '' },
          rop_paid: { value: !!data.rop_paid_at, remark: data.rop_paid_remark || '' },
          feedback: { value: !!data.feedback_at, remark: data.feedback_remark || '' },
        };

        setSelectedRow(data);
        setFormData(newFormData);
        setShowModal(true);
      } else {
        swal(i18n.t('error'), 'Failed to fetch auction details.', 'error');
      }
    } catch (error) {
      swal(i18n.t('error'), 'Error fetching auction details.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedRow(null);
    setShowModal(false);
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleCloseBulkModal = useCallback(() => {
    setAuctionSelected([]);
    setClearSelectedRows(!clearSelectedRows);
    setShowBulkModal(false);
    setFormData(INITIAL_FORM_DATA);
  }, [clearSelectedRows]);

  // Form handlers
  const handleToggle = useCallback((field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: !prev[field].value,
      },
    }));
  }, []);

  const handleRemarkChange = useCallback((field, remark) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        remark,
      },
    }));
  }, []);

  const handleSelectedAuctions = useCallback((e) => {
    const { selectedRows } = e;
    const selectedIds = selectedRows.map((row) => row.id);
    setAuctionSelected(selectedIds);
  }, []);

  // Update auction report
  const handleUpdateAuctionReport = useCallback(async () => {
    if (!selectedRow) return;

    setBtnLoading(true);
    try {
      const payload = Object.keys(formData).reduce((acc, key) => {
        acc[key] = {
          value: formData[key].value,
          remark: formData[key].remark,
        };
        return acc;
      }, {});

      const response = await updateAuctionReport(payload, selectedRow.id);
      if (response.success) {
        swal(i18n.t('success'), response.message, 'success');
        handleCloseModal();
        loadAllAuctions(); // Reload list
      } else {
        swal(i18n.t('error'), response.message, 'error');
      }
    } catch (error) {
      swal(i18n.t('error'), 'Error while updating.', 'error');
    } finally {
      setBtnLoading(false);
    }
  }, [selectedRow, formData, handleCloseModal, loadAllAuctions]);

  const handleUpdateAuctionReportBulk = useCallback(async () => {
    if (auctionSelected.length === 0) return;

    setBtnLoading(true);
    try {
      const payload = Object.keys(formData).reduce((acc, key) => {
        acc[key] = {
          value: formData[key].value,
          remark: formData[key].remark,
        };
        return acc;
      }, {});

      // Assuming updateAuctionReportBulk API exists and accepts array of IDs
      const response = await updateAuctionReportBulk(payload, auctionSelected);
      if (response.success) {
        swal(i18n.t('success'), response.message, 'success');
        handleCloseBulkModal();
        loadAllAuctions(); // reload table
      } else {
        swal(i18n.t('error'), response.message, 'error');
      }
    } catch (error) {
      swal(i18n.t('error'), 'Error while bulk updating.', 'error');
    } finally {
      setBtnLoading(false);
    }
  }, [auctionSelected, formData, handleCloseBulkModal, loadAllAuctions]);

  return (
    <>
      <HomeBanner />
      <section className="sptb mt-5">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-12 col-md-12">{!hideSidebar && <SideBar />}</div>
            <div className={hideSidebar ? 'col-xl-12' : 'col-xl-9 col-lg-12 col-md-12'}>
              <div className="card mb-0">
                {/* header  */}
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h3 className="card-title d-flex align-items-center m-0">
                    <span
                      onClick={() => setHideSidebar(!hideSidebar)}
                      style={{ cursor: 'pointer', fontSize: '1rem', marginRight: '0.5rem' }}
                    >
                      {hideSidebar ? <i className="icon-size-actual"></i> : <i className="icon-size-fullscreen"></i>}
                    </span>
                    {i18n.t('auction_report')}
                  </h3>
                </div>

                {/* Search and Filter Section */}
                <div className="search-bar">
                  <div className="container py-5">
                    <div className="row justify-content-center align-items-end g-3">
                      {/* Auction Group */}
                      <div className="col-sm-6 col-md-3">
                        <label className="form-label">{i18n.t('auction_group_name')}</label>
                        <select
                          className="form-select"
                          value={filters.group}
                          onChange={(e) => handleFilterChange('group', e.target.value)}
                        >
                          <option value="">{i18n.t('select_group')}</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {i18n.language === 'en' ? group.group_name : group.group_name_ar}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Auction Name */}
                      <div className="col-sm-6 col-md-3">
                        <label className="form-label">{i18n.t('auction_name')}</label>
                        <select
                          className="form-select"
                          value={filters.auction}
                          onChange={(e) => handleFilterChange('auction', e.target.value)}
                        >
                          <option value="">{i18n.t('select_auction')}</option>
                          {auctionOptions.length > 0 &&
                            auctionOptions.map((auction) => (
                              <option key={auction.id} value={auction.id}>
                                {auction.title}
                              </option>
                            ))}
                        </select>
                      </div>

                      {/* Date Filter */}
                      <div className="col-sm-6 col-md-3">
                        <label className="form-label">{i18n.t('date_range')}</label>
                        <select
                          className="form-select"
                          onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
                          value={filters.dateFilter || ''}
                        >
                          <option value="">{i18n.t('select_date_filter')}</option>
                          <option value="last7days">{i18n.t('last_week')}</option>
                          <option value="last30days">{i18n.t('last_month')}</option>
                          <option value="custom">{i18n.t('custom')}</option>
                        </select>

                        {/* Custom Date Range */}
                        {filters.dateFilter === 'custom' && (
                          <div className="row g-2 mt-2">
                            <div className="col-6">
                              <label className="form-label">{i18n.t('from')}</label>
                              <input
                                type="date"
                                className="form-control"
                                value={filters.fromDate || ''}
                                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                              />
                            </div>
                            <div className="col-6">
                              <label className="form-label">{i18n.t('to')}</label>
                              <input
                                type="date"
                                className="form-control"
                                value={filters.toDate || ''}
                                onChange={(e) => handleFilterChange('toDate', e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Clear Button */}
                      <div className="col-sm-6 col-md-3 d-flex justify-content-center align-items-end">
                        <button className="btn btn-secondary w-100" onClick={handleClearFilters}>
                          {i18n.t('clear')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* bulk update  */}

                  <div className="d-flex justify-content-between align-items-center w-100 mb-3  p-2">
                    <div className="d-flex align-items-end">
                      <button
                        className="btn btn-primary"
                        disabled={auctionSelected.length === 0}
                        onClick={handleOpenModalBulk}
                      >
                        {i18n.t('update_selected')}
                      </button>
                    </div>

                    {!isLoading && auctions.length > 0 && (
                      <div className="d-flex align-items-center">
                        <a
                          href={`${apiUrl}auction/reports/excel${qstring ? `?${qstring}` : ''}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fa fa-file-excel-o fa-2x text-success" />
                        </a>
                      </div>
                    )}
                  </div>

                  <MzadLoader animation="grow" size="md" loading={isLoading} />

                  {!isLoading && auctions.length === 0 && (
                    <div className="text-center py-4">
                      <p>{i18n.t('no_data_found')}</p>
                    </div>
                  )}

                  {!isLoading && auctions.length > 0 && (
                    <DataTable
                      columns={columns}
                      data={auctions}
                      customStyles={customStyles}
                      highlightOnHover
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={filters.limit}
                      paginationDefaultPage={currentPage}
                      paginationRowsPerPageOptions={[15, 25, 50, 100]}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handleRowsPerPageChange}
                      selectableRows
                      onSelectedRowsChange={handleSelectedAuctions}
                      clearSelectedRows={clearSelectedRows}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <EditAuctionStatus
        show={showModal}
        isLoading={isLoading}
        selectedRow={selectedRow}
        formData={formData}
        onClose={handleCloseModal}
        onToggle={handleToggle}
        onRemarkChange={handleRemarkChange}
        onSave={handleUpdateAuctionReport}
        btnLoading={btnLoading}
      />

      <EditAuctionStatusBulk
        show={showBulkModal}
        formData={formData}
        onClose={handleCloseBulkModal}
        onToggle={handleToggle}
        onRemarkChange={handleRemarkChange}
        onSave={handleUpdateAuctionReportBulk}
        btnLoading={btnLoading}
      />
    </>
  );
}
